import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Typography, Paper, Chip, Avatar, Tooltip } from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CallEndIcon from '@mui/icons-material/CallEnd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';

/**
 * HeldCallsPanel Component - Redesigned
 * 
 * Clean, modern panel showing calls currently on hold.
 * Matches the visual design of IncomingCallNotification for consistency.
 * 
 * Design System:
 * - Primary Amber: #F59E0B (on-hold status indicator)
 * - Success Green: #10B981 (resume action)
 * - Danger Red: #EF4444 (end action)
 * 
 * @param {Object} props
 * @param {Array} props.heldCalls - Array of held call objects
 * @param {Function} props.onResume - Callback to resume a held call
 * @param {Function} props.onEnd - Callback to end a held call
 */
const HeldCallsPanel = ({ heldCalls, onResume, onEnd }) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update time every second for hold duration counter
  useEffect(() => {
    if (!heldCalls || heldCalls.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, [heldCalls]);

  if (!heldCalls || heldCalls.length === 0) {
    return null;
  }

  const formatHoldDuration = (holdStartTime) => {
    const seconds = Math.floor((currentTime - holdStartTime) / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Design tokens - matching IncomingCallNotification
  const tokens = {
    primary: '#F59E0B', // Amber for on-hold status
    primaryLight: '#FEF3C7',
    primaryDark: '#D97706',
    success: '#10B981',
    successLight: '#D1FAE5',
    successDark: '#059669',
    danger: '#EF4444',
    dangerLight: '#FEE2E2',
    dangerDark: '#DC2626',
    neutral: '#6B7280',
    neutralLight: '#F3F4F6',
    neutralDark: '#4B5563',
    text: {
      primary: '#111827',
      secondary: '#6B7280',
      white: '#FFFFFF',
    },
    border: '#E5E7EB',
    background: '#FFFFFF',
    shadow: '0 4px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
    radius: '12px',
  };

  // Use Portal to render outside Dialog's stacking context
  const panelContent = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999999998, // Just below incoming call notification
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          width: 340, // Same width as IncomingCallNotification
          background: tokens.background,
          border: `1px solid ${tokens.border}`,
          borderRadius: tokens.radius,
          padding: 0,
          boxShadow: tokens.shadow,
          pointerEvents: 'auto',
          overflow: 'hidden',
          animation: 'slideInLeft 0.3s ease-out',
          '@keyframes slideInLeft': {
            from: {
              transform: 'translateX(-400px)',
              opacity: 0,
            },
            to: {
              transform: 'translateX(0)',
              opacity: 1,
            },
          },
        }}
      >
        {/* Header - Clean and minimal with amber theme */}
        <Box
          sx={{
            background: tokens.primaryLight,
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${tokens.border}`,
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              background: tokens.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PauseCircleOutlineIcon sx={{ fontSize: 18, color: tokens.text.white }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              color: tokens.primaryDark,
              flex: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Calls On Hold
          </Typography>
          <Chip
            label={heldCalls.length}
            size="small"
            sx={{
              height: 24,
              minWidth: 24,
              fontSize: '12px',
              fontWeight: 700,
              background: tokens.primary,
              color: tokens.text.white,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        </Box>

        {/* Held Calls List */}
        <Box sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {heldCalls.map((call, index) => (
            <Box
              key={call.pucode}
              sx={{
                padding: '16px',
                borderBottom: index < heldCalls.length - 1 ? `1px solid ${tokens.border}` : 'none',
                '&:hover': {
                  background: tokens.neutralLight,
                },
                transition: 'background 0.15s ease',
              }}
            >
              {/* Patient Info Row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    background: `linear-gradient(135deg, ${tokens.primary} 0%, ${tokens.primaryDark} 100%)`,
                    fontSize: '15px',
                    fontWeight: 600,
                    border: `2px solid ${tokens.primaryLight}`,
                  }}
                >
                  {call.patientName
                    ? call.patientName
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()
                        .substring(0, 2)
                    : <PersonIcon sx={{ fontSize: 20 }} />}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: tokens.text.primary,
                      fontSize: '15px',
                      lineHeight: 1.3,
                      mb: 0.5,
                    }}
                    noWrap
                  >
                    {call.patientName || 'Unknown Patient'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <AccessTimeIcon sx={{ fontSize: 14, color: tokens.primary }} />
                    <Typography
                      sx={{
                        color: tokens.primary,
                        fontSize: '13px',
                        fontWeight: 600,
                      }}
                    >
                      On hold: {formatHoldDuration(call.holdStartTime)}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons - Full width with labels */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Tooltip title="Resume this call" arrow placement="top">
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                    onClick={() => onResume && onResume(call.pucode)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      background: tokens.success,
                      boxShadow: 'none',
                      minHeight: 42,
                      '&:hover': {
                        background: tokens.successDark,
                        boxShadow: `0 4px 12px ${tokens.success}40`,
                      },
                    }}
                  >
                    Resume
                  </Button>
                </Tooltip>
                <Tooltip title="End this held call" arrow placement="top">
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CallEndIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onEnd && onEnd(call.pucode)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      minHeight: 42,
                      borderColor: tokens.danger,
                      color: tokens.danger,
                      '&:hover': {
                        borderColor: tokens.dangerDark,
                        background: tokens.dangerLight,
                      },
                    }}
                  >
                    End Call
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Footer hint if multiple calls */}
        {heldCalls.length > 1 && (
          <Box
            sx={{
              padding: '10px 16px',
              background: tokens.neutralLight,
              borderTop: `1px solid ${tokens.border}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                color: tokens.text.secondary,
                textAlign: 'center',
                fontWeight: 500,
              }}
            >
              {heldCalls.length} calls waiting • Scroll to see all
            </Typography>
          </Box>
        )}
      </Paper>
    </div>
  );

  // Render using Portal to ensure it's above Dialog
  return ReactDOM.createPortal(panelContent, document.body);
};

export default HeldCallsPanel;



// WEBPACK FOOTER //
// ./src/components/home/HeldCallsPanel.js